import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Flex,
  Text,
  Checkbox,
  Select,
  Option,
  Configuration,
  Label,
  RadioButtonGroup,
  RadioButton,
  ModalManagerContext,
  Button,
} from '@pega/cosmos-react-core';
import { useDispatch, useSelector } from 'react-redux';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { appState, customModalProps } from '../../../../interfaces/common';
import { updateUIToggle } from '../ChatApis';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import { CustomModal } from '../../../Utilities/CustomModal';
import {
  updateEmojiIconPayload,
  updateAttachmentIconPayload,
  updateFooterTextPayload,
  updateTypingIndicatorPayload,
  updateChatLauncherPayload,
  updateWidgetPositionPayload,
  enableLanguageSelectionPayload,
  updateDownloadTranscriptPayload,
  disableVirusScanPayload,
  TypingBeforeWelcomeMessagePayload,
  GenAIWatermarkPayload,
  updateEncryptAttachmentsPayload,
  UpdateWidgetDimension,
  AllowChangingWidgetSize,
  EnableVoiceToTextPayload,
  EnableVoiceNotesPayload,
  HasToggleWithoutIframe,
  AgenticLoaderPayload,
  EnableHeartBeatPayload,
} from '../../../../interfaces/chat';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { usePlatformVersionSelector } from '../../../../redux/selectors';
import WidgetButtonConfig from './WidgetButtonConfig';
import EnableAvatar from './EnableAvatar';
import QuickReplyConfig from './QuickReplyConfig';
import UploadFontFile from '../Modals/UploadFontFile';
import CustomTooltip from '../../../Utilities/CustomTooltip';
import { platformVersionCheck } from '../../../../utils/helpers';
import AdditionalInfo from '../../../cosmos-core/AdditionalInfo';
import AgenticLoaderConfig from './AgenticLoaderConfig';
import styled, { css } from 'styled-components';

export interface DisplayTogglesProps {
  showSDKConfigs?: boolean;
  displayTitle?: boolean;
  onAgenticPreviewChange?: (enabled: boolean, phrases: string[]) => void;
}

export const tabConfig = {
  General: 'General',
  Styling: 'Styling',
  Language: 'Language',
};

export const StyledRadioButtonGroup = styled(RadioButtonGroup)(
  () =>
    css`
      margin: 0;
    `,
);

export const StyledFlex = styled(Flex)(
  () => css`
    margin-block-start: 16px;
  `,
);

const DisplayToggles: React.FC<DisplayTogglesProps> = ({
  showSDKConfigs,
  displayTitle = true,
  onAgenticPreviewChange,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const translate = useLocalTranslation();
  const modalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const { create } = useContext(ModalManagerContext);
  const {
    encryptionEnableFlag,
    chatInfo: {
      connectionId,
      emojiIcon,
      attachmentIcon,
      footerText,
      typingIndicator,
      hideChatLauncher,
      widgetPosition,
      enableLanguageSelection,
      displayDownloadTranscript,
      encryptionEnabled,
      disableVirusScan,
      allowChangingWidgetSize,
      languageTranslationEnabled,
      typingBeforeWelcomeMessage,
      disableGenAIWatermark,
      encryptAttachments,
      enableMultiExperienceWidget,
      widgetDimension = 'Standard',
      enableVoiceToText,
      enableVoiceNotes,
      hasToggleWithoutIframe,
      isAgenticMessaging,
      enableMultiPhaseLoader,
      dynamicPhrases,
      enableHeartBeat,
    },
    sessionId,
    tabName,
  } = useSelector((state: appState) => {
    return {
      chatInfo: state.chatInfo,
      sessionId: state.sessionId,
      encryptionEnableFlag: state.encryptionEnableFlag,
      tabName: state.tabName,
    };
  });
  const byokEncryptionEnabled = encryptionEnableFlag || encryptionEnabled;
  const forSDKWithEncryption = showSDKConfigs && byokEncryptionEnabled;
  const showAttachmentAndTranscriptToggles = !showSDKConfigs || forSDKWithEncryption;
  const [widgetPos, setWidgetPosition] = useState('');

  useEffect(() => {
    if (widgetPosition) setWidgetPosition(widgetPosition);
  }, [widgetPosition]);

  const { fullVersion, majorVersion, minorVersion, patchVersion } = usePlatformVersionSelector();
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));
  const beyondEightDotEight =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 8));
  const beyondEightDotSixTwo = Boolean(fullVersion && fullVersion === 8.6 && patchVersion && patchVersion >= 2);
  const showQuickReplyConfigSDK =
    showSDKConfigs && parseInt(process.env.REACT_APP_ENABLE_QUICK_REPLY_DROPDOWN || '1') !== 0;
  const showQuickReplyConfig = !showSDKConfigs || showQuickReplyConfigSDK;

  // for 24.1 CS version platform version is 8.24.0
  const fromVersion8241 = platformVersionCheck(8, 24, 0, majorVersion, minorVersion, patchVersion);

  const updateEmojiIcon = async (e: any) => {
    await updateToggleConfig('emoji_icon', { emojiIcon: e.target.checked });
  };

  const updateAttachmentIcon = async (e: any) => {
    await updateToggleConfig('attachment_icon', { attachmentIcon: e.target.checked });
  };

  const updateEncryptAttachments = async (e: any) => {
    await updateToggleConfig('encrypt_attachment', { encryptAttachments: e.target.checked });
  };

  const updateFooterText = async (e: any) => {
    await updateToggleConfig('footer_text', { footerText: e.target.checked });
  };

  const updateTypingIndicator = async (e: any) => {
    await updateToggleConfig('typing_indicator', { typingIndicator: e.target.checked });
  };

  const updateChatLauncher = async (e: any) => {
    await updateToggleConfig('hide_chat_launcher', { hideChatLauncher: e.target.checked });
  };

  const updateWidgetPosition = async (e: any) => {
    await updateToggleConfig('widget_position', { widgetPosition: e });
  };

  const updateLanguageOption = async (e: any) => {
    await updateToggleConfig('language_selection', { enableLanguageSelection: e.target.checked });
  };

  const updateDownloadTranscriptOption = async (e: any) => {
    await updateToggleConfig('display_download_transcript', { displayDownloadTranscript: e.target.checked });
  };

  const updateDisableVirusScan = async (e: any) => {
    await updateToggleConfig('virus_scan', { disableVirusScan: e.target.checked });
  };

  const updateAllowChangingWidgetSize = async (e: any) => {
    await updateToggleConfig('allow_changing_widget_size', { allowChangingWidgetSize: e.target.checked });
  };

  const updateDisableGenAIWatermark = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await updateToggleConfig('genai_watermark', { disableGenAIWatermark: e.target.checked });
  };

  const updateTypingBeforeWelcomeMessage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await updateToggleConfig('allow_typing_before_welcome_message', {
      typingBeforeWelcomeMessage: e?.target?.checked,
    });
  };

  const updateEnableVoiceToText = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await updateToggleConfig('enable_voice_to_text', {
      enableVoiceToText: e?.target?.checked,
    });
  };

  const updateEnableVoiceNotes = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await updateToggleConfig('enable_voice_notes', {
      enableVoiceNotes: e?.target?.checked,
    });
  };

  const updateHasToggleWithoutIframe = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await updateToggleConfig('toggle_without_iframe', {
      hasToggleWithoutIframe: e?.target?.checked,
    });
  };

  const updateEnableHeartBeat = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const enableHeartBeatChecked = e.target.checked;
    if (enableHeartBeatChecked) {
      modalMethods.current = create<customModalProps>(CustomModal, {
        heading: translate('chat_heart_beat_confirm'),
        children: (
          <>
            <Text>{translate('chat_heart_beat_confirmation_intro')}</Text>
            <ul style={{ marginTop: '8px', paddingInlineStart: '20px' }}>
              <li>
                <Text>{translate('chat_heart_beat_confirmation_point1')}</Text>
              </li>
              <li>
                <Text>{translate('chat_heart_beat_confirmation_point2')}</Text>
              </li>
            </ul>
          </>
        ),
        actions: (
          <>
            <Button
              onClick={() => {
                modalMethods.current?.dismiss();
              }}
            >
              {translate('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                await updateToggleConfig('enable_heart_beat', {
                  enableHeartBeat: enableHeartBeatChecked,
                });
                modalMethods.current?.dismiss();
              }}
            >
              {translate('submit')}
            </Button>
          </>
        ),
      });
    } else {
      await updateToggleConfig('enable_heart_beat', {
        enableHeartBeat: enableHeartBeatChecked,
      });
    }
  };

  const updateToggleConfig = async (
    togglePath: string,
    payload:
      | updateEmojiIconPayload
      | updateAttachmentIconPayload
      | updateFooterTextPayload
      | updateChatLauncherPayload
      | updateTypingIndicatorPayload
      | updateWidgetPositionPayload
      | enableLanguageSelectionPayload
      | updateDownloadTranscriptPayload
      | disableVirusScanPayload
      | TypingBeforeWelcomeMessagePayload
      | GenAIWatermarkPayload
      | updateEncryptAttachmentsPayload
      | AllowChangingWidgetSize
      | UpdateWidgetDimension
      | EnableVoiceToTextPayload
      | EnableVoiceNotesPayload
      | HasToggleWithoutIframe
      | AgenticLoaderPayload
      | EnableHeartBeatPayload,
  ) => {
    dispatch(updateLoaderDetails(true));
    const json = await updateUIToggle(togglePath, sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate(`${togglePath}_update_success`), 'success'));
      return true;
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
      return false;
    } else {
      dispatch(updateBannerInfo(translate(`${togglePath}_update_failed`), 'urgent'));
      return false;
    }
  };

  const widgetSizeChangeHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await updateToggleConfig('widget_dimension', {
      widgetDimension: event?.target?.value,
    });
  };

  const updateAgenticLoaderConfig = async (enabled: boolean, phrases: string[]) => {
    return await updateToggleConfig('agentic_loader', {
      enableMultiPhaseLoader: enabled,
      dynamicPhrases: phrases,
    });
  };

  const handleAgenticLoaderToggle = (enabled: boolean, phrases: string[]) => {
    return updateAgenticLoaderConfig(enabled, phrases ?? []);
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 1,
      }}
    >
      {displayTitle && (
        <Text variant="h3" as="h3">
          {translate(
            showSDKConfigs ? 'sdk_config' : tabName === tabConfig.General ? 'widget_config' : 'widget_style_config',
          )}
        </Text>
      )}
      {!showSDKConfigs && tabName === tabConfig.General && (
        <Checkbox
          label={translate('display_emoji_icon')}
          name="emojiToggle"
          checked={emojiIcon}
          onChange={updateEmojiIcon}
        />
      )}
      {showAttachmentAndTranscriptToggles && tabName === tabConfig.General && (
        <Flex direction="column">
          <Checkbox
            label={translate('display_download_transcript')}
            disabled={languageTranslationEnabled}
            checked={displayDownloadTranscript && !languageTranslationEnabled}
            name="displayDownloadTranscript"
            onChange={updateDownloadTranscriptOption}
          />
          {languageTranslationEnabled && <Label>{translate('read_only_download_transcript')}</Label>}
        </Flex>
      )}

      {tabName === tabConfig.General && (
        <Checkbox
          name="attachmentToggle"
          label={translate('display_attachment_icon')}
          checked={attachmentIcon}
          onChange={updateAttachmentIcon}
        />
      )}

      {tabName === tabConfig.General && byokEncryptionEnabled && (
        <Checkbox
          name="encrypt_attachment"
          label={translate('byok_attachment')}
          checked={encryptAttachments}
          onChange={updateEncryptAttachments}
        />
      )}

      {!showSDKConfigs && (
        <>
          {tabName === tabConfig.Styling && (
            <Checkbox
              label={translate('display_footer_text')}
              name="footerToggle"
              checked={footerText}
              onChange={updateFooterText}
            />
          )}
          {(beyondEightDotSeven || beyondEightDotSixTwo) && tabName === tabConfig.General && (
            <Checkbox
              label={translate('display_typing_indicator')}
              checked={typingIndicator}
              name="typingIndicatorToggle"
              onChange={updateTypingIndicator}
            />
          )}
          {tabName === tabConfig.Styling && (
            <Checkbox
              label={translate('hide_chat_launcher')}
              name="chatLauncherToggle"
              checked={hideChatLauncher}
              onChange={updateChatLauncher}
            />
          )}
          {beyondEightDotSeven && tabName && [tabConfig.Language, tabConfig.General].includes(tabName) && (
            <Configuration
              theme={{
                components:
                  tabConfig.Language === tabName
                    ? {
                        'radio-check': {
                          label: {
                            'font-weight': 'bold',
                          },
                          'border-width': '0.1rem',
                          'border-color': '#000',
                        },
                      }
                    : undefined,
              }}
            >
              <Checkbox
                name="languageSelection"
                label={translate('language_option')}
                checked={enableLanguageSelection}
                onChange={updateLanguageOption}
              />
            </Configuration>
          )}
        </>
      )}
      {beyondEightDotSeven && tabName === tabConfig.General && !showSDKConfigs && (
        <Checkbox
          label={translate('allow_typing_before_welcome_message')}
          checked={typingBeforeWelcomeMessage}
          name="Allow Typing"
          onChange={updateTypingBeforeWelcomeMessage}
        />
      )}
      {tabName === tabConfig.Styling && fromVersion8241 && (
        <Flex
          container={{
            itemGap: 1,
          }}
        >
          <Checkbox
            label={translate('disable_genAI_watermark')}
            name="disableGenAIWatermark"
            checked={disableGenAIWatermark}
            onChange={updateDisableGenAIWatermark}
          />
          <CustomTooltip
            info={translate('disable_genAI_watermark_note')}
            tooltipStyle={{ margin: '5px' }}
            iconName="help"
          />
        </Flex>
      )}

      {!showSDKConfigs && tabName === tabConfig.General && (
        <Checkbox
          label={translate('enable_heart_beat')}
          name="enableHeartBeatToggle"
          checked={enableHeartBeat}
          onChange={updateEnableHeartBeat}
        />
      )}

      {tabName === tabConfig.General && !encryptionEnabled && (
        <>
          <Checkbox
            label={translate('virus_scan')}
            name="disableVirusScan"
            checked={disableVirusScan}
            onChange={updateDisableVirusScan}
          />
          <Text>
            <b>{translate('note')}:</b> {translate('virus_scan_note')}
          </Text>
        </>
      )}
      {tabName === tabConfig.Styling && !showSDKConfigs && !enableMultiExperienceWidget && (
        <Checkbox
          label={translate('allow_changing_widget_size')}
          name="allow_changing_widget_size"
          checked={allowChangingWidgetSize}
          onChange={updateAllowChangingWidgetSize}
        />
      )}
      {(tabName === tabConfig.Styling || showSDKConfigs) && <EnableAvatar showSDKConfigs={showSDKConfigs} />}
      {(tabName === tabConfig.Styling || showSDKConfigs) && isAgenticMessaging && (
        <AgenticLoaderConfig
          key={connectionId}
          initialEnabled={enableMultiPhaseLoader}
          initialPhrases={dynamicPhrases}
          onChange={onAgenticPreviewChange}
          onToggle={handleAgenticLoaderToggle}
          onSave={updateAgenticLoaderConfig}
        />
      )}
      {tabName === tabConfig.Styling && !showSDKConfigs && !enableMultiExperienceWidget && (
        <>
          <StyledFlex container={{ direction: 'column' }}>
            <Text variant={'h3'}>{translate('widget_size_configuration')}</Text>
            <StyledFlex container>
              <Label> {translate('widget_size')} </Label>
              <AdditionalInfo heading={translate('widget_size_configuration')}>
                <>
                  <Flex>{translate('configuration_definition_0')}</Flex>
                  <Flex>{translate('configuration_definition_1')}</Flex>
                  <Flex>{translate('configuration_definition_2')}</Flex>
                  <br />
                  <Flex>
                    <b>{translate('standard')}</b>
                    {translate('configuration_definition_3', 380)}
                  </Flex>
                  <Flex>
                    <b>{translate('large')}</b>
                    {translate('configuration_definition_4', 565)}
                  </Flex>
                  <Flex>
                    <b>{translate('extra_large')}</b>
                    {translate('configuration_definition_5', 1120)}
                  </Flex>
                </>
              </AdditionalInfo>
            </StyledFlex>
          </StyledFlex>
          <StyledRadioButtonGroup labelHidden={true} name="size-configuration-group" onChange={widgetSizeChangeHandler}>
            <RadioButton
              label="Standard"
              id="standard"
              value="Standard"
              checked={widgetDimension === 'Standard'}
              data-testid="standard"
            />
            <RadioButton
              label="Large"
              value="Large"
              checked={widgetDimension === 'Large'}
              id="large"
              data-testid="large"
            />
            <RadioButton
              label="Extra Large"
              id="extra-large"
              value="Extra Large"
              checked={widgetDimension === 'Extra Large'}
              data-testid="extra-large"
            />
          </StyledRadioButtonGroup>
        </>
      )}
      {connectionId && tabName === tabConfig.Styling && <UploadFontFile />}
      {!showSDKConfigs && tabName === tabConfig.Styling && (
        <>
          <Flex
            container={{
              direction: 'row',
              alignItems: 'start',
              itemGap: 0.5,
            }}
          >
            <Flex item={{ grow: 0.4 }}>
              <Select
                label={translate('widget_position')}
                value={widgetPos}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  updateWidgetPosition(e.target.value);
                }}
              >
                <Option value="bottom_right">{translate('bottom_right')}</Option>
                <Option value="bottom_left">{translate('bottom_left')}</Option>
                <Option value="top_right">{translate('top_right')}</Option>
                <Option value="top_left">{translate('top_left')}</Option>
                <Option value="center_right">{translate('center_right')}</Option>
                <Option value="center_left">{translate('center_left')}</Option>
              </Select>
            </Flex>
          </Flex>
          <WidgetButtonConfig />
        </>
      )}
      {((showQuickReplyConfig && tabName === tabConfig.General && showSDKConfigs) || tabName === tabConfig.Styling) && (
        <QuickReplyConfig />
      )}
      {beyondEightDotEight && tabName === tabConfig.General && !showSDKConfigs && (
        <Checkbox
          label={translate('enable_voice_to_text')}
          checked={enableVoiceToText}
          name="voice to text"
          onChange={updateEnableVoiceToText}
        />
      )}
      {showSDKConfigs && tabName === tabConfig.General && (
        <Checkbox
          label={translate('enable_voice_notes')}
          checked={enableVoiceNotes}
          name="voice notes"
          onChange={updateEnableVoiceNotes}
        />
      )}
      {tabName === tabConfig.General && !showSDKConfigs && !enableMultiExperienceWidget && (
        <Checkbox
          name="toggleWithoutIframe"
          label={translate('toggle_without_iframe')}
          checked={hasToggleWithoutIframe}
          onChange={updateHasToggleWithoutIframe}
        />
      )}
    </Flex>
  );
};

export default DisplayToggles;
